import * as React from 'react';
import { Image, Text, Icon, Link } from '@blueprint/ui';
import { Badge } from '@blueprint/badge';
import {
    AgencyLogo,
    CardDetails,
    CardHeader,
    PriceContainer,
    PriceTitleText,
    StyledTitle,
    StyledCard,
    IconContainer,
    FeaturesContainer,
    ImageContainer,
    CustomButton,
    BadgeWrapper
} from './ListingCardPortraitMini.styles';

import { ListingFeatureList } from '@zoopla/listings/features';
import { ListingImage } from '@zoopla/listings/image';

import {
    ListingCardPortraitMiniIconType,
    ListingCardPortraitMiniProps
} from './ListingCardPortraitMini.types';

const defaultHandler = () => null;

const ListingCardPortraitMini: React.FC<ListingCardPortraitMiniProps> = ({
    link,
    address,
    features,
    image,
    branch,
    price,
    priceTitle,
    title,
    variant,
    showIconType = ListingCardPortraitMiniIconType.BranchIcon,
    isListingSaved = false,
    onImageClick = defaultHandler,
    onCardClick = defaultHandler,
    onFeaturesClick = defaultHandler,
    onTitleClick = defaultHandler,
    onPriceClick = defaultHandler,
    onSaveClick = defaultHandler,
    onAgentClick = defaultHandler,
    onAddressClick = defaultHandler,
    onLinkClick = defaultHandler
}) => {
    const buildCardIcon = () => {
        if (showIconType === ListingCardPortraitMiniIconType.BranchIcon) {
            if (branch?.logoUrl) {
                return (
                    <AgencyLogo>
                        <Image
                            onClick={onAgentClick}
                            data-testid={'agent-img'}
                            src={branch?.logoUrl}
                            alt={`Marketed by ${branch?.name}`}
                        />
                    </AgencyLogo>
                );
            }
        } else if (
            showIconType === ListingCardPortraitMiniIconType.FavouriteIcon
        ) {
            const iconId = isListingSaved ? 'save_filled' : 'save_outline';
            return (
                <IconContainer>
                    <CustomButton
                        onClick={onSaveClick}
                        data-testid="favourite"
                        variant="tertiary"
                        aria-label="Menu"
                        iconOnly={true}
                    >
                        <Icon iconId={iconId} size="large" />
                    </CustomButton>
                </IconContainer>
            );
        }
        return null;
    };

    const cardIcon = buildCardIcon();

    return (
        <StyledCard
            hasShadow={false}
            hasBorder
            hasBackground
            data-testid={'listing-card-portrait-mini'}
            onClick={onCardClick}
        >
            <Link
                plain
                href={link}
                onClick={onLinkClick}
                data-testid="link"
                target="_blank"
            >
                <ImageContainer onClick={onImageClick}>
                    <ListingImage image={image} />
                    {variant && (
                        <BadgeWrapper>
                            <Badge variant={variant} />
                        </BadgeWrapper>
                    )}
                </ImageContainer>
                <CardDetails>
                    <CardHeader
                        data-testid="price-title"
                        onClick={onPriceClick}
                    >
                        <PriceContainer>
                            {priceTitle && (
                                <PriceTitleText>{priceTitle}</PriceTitleText>
                            )}
                            <Text size="6" isBold>
                                {price}
                            </Text>
                        </PriceContainer>
                    </CardHeader>
                    {cardIcon}
                    <FeaturesContainer
                        data-testid={'features'}
                        onClick={onFeaturesClick}
                    >
                        <ListingFeatureList features={features} />
                    </FeaturesContainer>

                    {title && (
                        <StyledTitle
                            size="8"
                            data-testid={'title'}
                            onClick={onTitleClick}
                        >
                            {title}
                        </StyledTitle>
                    )}
                    {address && (
                        <Text
                            size="secondary"
                            color={'text-body'}
                            data-testid={'address'}
                            onClick={onAddressClick}
                        >
                            {address}
                        </Text>
                    )}
                </CardDetails>
            </Link>
        </StyledCard>
    );
};

export default ListingCardPortraitMini;
